import React from "react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { Home } from "lucide-react";

interface BreadcrumbProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  currentPath,
  onNavigate,
}) => {
  const { isDark } = useTheme();
  const pathParts = currentPath.split("/").filter(Boolean);

  // Function to transform path parts for display
  // Hide store/storeType/storeId but show everything after it
  const transformPathForDisplay = (parts: string[]) => {
    if (parts.length >= 3) {
      // Check if the first three parts match store/storeType/storeId pattern
      const storePattern = /^store$/;
      const storeTypePattern = /^(seller|wholesaler)$/;
      const storeIdPattern = /^[a-f0-9-]{36}$/; // UUID pattern

      if (storePattern.test(parts[0]) && storeTypePattern.test(parts[1]) && storeIdPattern.test(parts[2])) {
        // Return only the parts after store/storeType/storeId (could be empty array)
        return parts.slice(3);
      }
    }
    // If it doesn't match the pattern, return empty array to hide everything
    return [];
  };

  const displayParts = transformPathForDisplay(pathParts);

  const buildPath = (index: number) => {
    // Build the full path including the hidden store/storeType/storeId parts
    const hiddenParts = pathParts.slice(0, pathParts.length - displayParts.length);
    const visibleParts = displayParts.slice(0, index + 1);
    return "/" + [...hiddenParts, ...visibleParts].join("/");
  };

  return (
    <nav
      className={`flex items-center space-x-1 text-sm overflow-x-auto pb-2 ${isDark ? "text-gray-200" : "text-gray-600"}`}
    >
      <Button
        variant="ghost"
        size="sm"
        className="h-8 px-3 text-blue-600 hover:text-blue-800 hover:bg-blue-50 whitespace-nowrap"
        onClick={() => onNavigate("/")}
      >
        <Home className="w-4 h-4 mr-1" />
        Root
      </Button>

      {displayParts.map((part, index) => (
        <React.Fragment key={index}>
          <span
            className={`flex-shrink-0 ${isDark ? "text-gray-400" : "text-gray-400"}`}
          >
            /
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-blue-600 hover:text-blue-800 hover:bg-blue-50 max-w-[150px] truncate"
            onClick={() => onNavigate(buildPath(index))}
            title={part}
          >
            {part}
          </Button>
        </React.Fragment>
      ))}
    </nav>
  );
};
